import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import type { RootState } from '../../redux/store';
import PageHeader from '../common/PageHeader';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import ErrorState from '../common/ErrorState';

/**
 * Menu Display Page
 * Shows the menu items pulled from Clover integration
 */
const Menu: React.FC = () => {
  const { items, loading, error } = useSelector(
    (state: RootState) => state.menuItems
  );
  const { restaurantId } = useSelector((state: RootState) => state.clover);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Loading menu..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">Failed to Load Menu</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="px-6 py-3 bg-[#EE4423] text-white rounded-lg hover:bg-[#d63d20] transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!items || items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">No Menu Items</h2>
          <p className="text-gray-400 mb-6">Your menu is empty. Please check your Clover account.</p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="px-6 py-3 bg-[#EE4423] text-white rounded-lg hover:bg-[#d63d20] transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <PageHeader
          title={`Your Menu - ${items.length} items`}
        />
        
        {/* Subtitle */}
        <p className="text-gray-400 text-sm mb-6 -mt-4 ml-6">
          Restaurant ID: {restaurantId || 'N/A'}
        </p>

        {/* Menu Items Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8"
        >
          {items.map((item, index) => (
            <motion.div
              key={item.pk_id || index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700 hover:border-[#EE4423] transition-all duration-300 hover:shadow-lg hover:shadow-[#EE4423]/20"
            >
              {/* Item Name */}
              <h3 className="text-xl font-bold text-white mb-2">
                {item.name || 'Unnamed Item'}
              </h3>

              {/* Item Price */}
              {item.price && (
                <p className="text-[#EE4423] font-semibold text-lg mb-3">
                  ${parseFloat(item.price).toFixed(2)}
                </p>
              )}

              {/* Item Description */}
              {item.description && (
                <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                  {item.description}
                </p>
              )}

              {/* Item Category */}
              {item.category && (
                <div className="mt-4">
                  <span className="inline-block px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full border border-gray-700">
                    {item.category}
                  </span>
                </div>
              )}

              {/* Item Details */}
              <div className="mt-4 pt-4 border-t border-gray-700 space-y-2 text-xs text-gray-500">
                {item.pk_id && (
                  <div className="flex justify-between">
                    <span>ID:</span>
                    <span className="text-gray-400">{item.pk_id}</span>
                  </div>
                )}
                {item.external_id && (
                  <div className="flex justify-between">
                    <span>External ID:</span>
                    <span className="text-gray-400 truncate max-w-[150px]">{item.external_id}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 p-6 bg-gradient-to-r from-green-900/20 to-green-800/20 rounded-xl border border-green-700/50"
        >
          <div className="flex items-center gap-3">
            <svg
              className="w-6 h-6 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h4 className="text-green-500 font-semibold">
                Clover Integration Successful!
              </h4>
              <p className="text-gray-400 text-sm mt-1">
                Your menu has been successfully synced from Clover.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Menu;
